import type { ReactNode } from "react";

/**
 * The divider between blocks of a screen: a title, a rule, and an optional count.
 *
 * The same silhouette as the schedule's day header, because a member scrolling the Payments or
 * Profile tab is doing the same thing they do on the agenda, which is looking for the boundary
 * between one kind of thing and the next.
 *
 * Not sticky here. The schedule sticks its day headers because the answer to "which day am I
 * looking at" is worth a permanent line of the viewport; "Due" and "Paid" are not.
 */
export function SectionHeader({
  title,
  detail,
  action,
}: {
  title: string;
  detail?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex items-center gap-3 pt-1 pb-2.5">
      <h2 className="text-[15px] font-semibold">{title}</h2>
      <span className="h-px flex-1 bg-line" aria-hidden="true" />
      {detail ? (
        <span className="text-[11px] font-semibold tracking-[0.08em] text-ink-3 uppercase">
          {detail}
        </span>
      ) : null}
      {action}
    </div>
  );
}

/**
 * The day divider on an agenda: a date, a rule, and a count.
 *
 * Sticky, unlike the page header and unlike `SectionHeader`. While scrolling two months of
 * sessions the one thing worth keeping on screen is which day you are looking at. Shared by the
 * schedule and the Book tab so the two lists cannot drift apart.
 */
export function DayHeader({ label, count }: { label: string; count: number }) {
  return (
    <div className="sticky top-(--sticky-top) z-10 -mx-5 bg-surface/95 px-5 py-2 backdrop-blur-sm">
      <div className="flex items-center gap-3">
        <h2 className="text-[15px] font-semibold">{label}</h2>
        <span className="h-px flex-1 bg-line" aria-hidden="true" />
        <span className="text-[11px] font-semibold tracking-[0.08em] text-ink-3 uppercase">
          {count} {count === 1 ? "session" : "sessions"}
        </span>
      </div>
    </div>
  );
}

export type NoticeTone = "info" | "warning" | "danger" | "success";

/** Every tone a message box can have. `quiet` is for context, not for news. */
export type NoticeCoinTone = NoticeTone | "quiet";

const GLYPHS: Record<NoticeCoinTone, ReactNode> = {
  info: (
    <>
      <path d="M12 10.5v7" />
      <path d="M12 6.5h.01" />
    </>
  ),
  quiet: (
    <>
      <path d="M12 10.5v7" />
      <path d="M12 6.5h.01" />
    </>
  ),
  success: <path d="m6.5 12.5 3.5 3.5 7.5-8" />,
  warning: (
    <>
      <path d="M12 6.5v7" />
      <path d="M12 17.5h.01" />
    </>
  ),
  danger: (
    <>
      <path d="M12 6.5v7" />
      <path d="M12 17.5h.01" />
    </>
  ),
};

/**
 * The raised coin that holds a message box's icon (`.notice-coin` in styles.css): "i" for info and
 * quiet, a tick for success, "!" for warning and danger. Inside a box it takes the box's tone;
 * alone, pass `tone`.
 */
export function NoticeCoin({ tone }: { tone: NoticeCoinTone }) {
  return (
    <span className={`notice-coin notice-${tone}`} aria-hidden="true">
      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
        {GLYPHS[tone]}
      </svg>
    </span>
  );
}

/** The gold coin with a "!" that every warning box carries. */
export function WarningCoin() {
  return <NoticeCoin tone="warning" />;
}

/**
 * An inline message about what just happened, or is about to.
 *
 * Distinct from `ErrorState`, which replaces a screen that has nothing to show. A notice sits above
 * content that is still there and still usable: a payment confirming, a booking that was refused, a
 * policy consequence the member is being asked to accept. Screens never catch errors to build one
 * of these from scratch; they pass the error through `copyForError` and render the result.
 *
 * Every tone is drawn raised, with its icon in a coin (`.notice` in styles.css). The words are a
 * dark ink of the tone, so the coin and the box colour say "look here".
 *
 * `action` puts what to do about the message inside the same box, under the words, full width
 * (a `Button`): one box says what happened and what to do next. `aside` is a short value at the
 * end of the title line, such as a count ("6 left").
 */
export function Notice({
  tone = "info",
  title,
  children,
  icon,
  action,
  aside,
}: {
  tone?: NoticeTone;
  title: string;
  children?: ReactNode;
  /** Another icon in the coin, when the tone's own glyph says less (a lock for a closed step). */
  icon?: ReactNode;
  /** The next step, inside the box under the words. */
  action?: ReactNode;
  /** A short value at the end of the title line, in the box's ink: a count, an amount. */
  aside?: ReactNode;
}) {
  return (
    <div
      role={tone === "danger" ? "alert" : "status"}
      className={`notice notice-${tone} flex flex-col gap-3 rounded-card px-3.5 py-3`}
    >
      <div className="flex gap-2.5">
        <span className="mt-px shrink-0">
          {icon ? (
            <span className={`notice-coin notice-${tone} [&_svg]:size-3.5`} aria-hidden="true">
              {icon}
            </span>
          ) : (
            <NoticeCoin tone={tone} />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-semibold">{title}</p>
          {children ? <div className="mt-1 text-[13px] leading-5 opacity-90">{children}</div> : null}
        </div>
        {aside ? <span className="shrink-0 text-[14px] font-semibold tabular-nums">{aside}</span> : null}
      </div>
      {action ? <div className="flex flex-col">{action}</div> : null}
    </div>
  );
}
